import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  Comprador, InsertComprador, UpdateComprador,
  ProdutoVenda, InsertProdutoVenda, UpdateProdutoVenda,
  InsertVenda, UpdateVenda, VendaWithRelations, VariedadeWithCultura,
} from "@shared/schema";
import { filtrarVariedadesDoCacho } from "@shared/culturas";

// Itens e colheita associada seguem a mesma regra de edição (mesmo backend, updateVenda):
// RASCUNHO ou EMITIDA-não-paga. Só PAGA/CANCELADA são imutáveis (exceto despagar por ADMIN).
// Helper único pra não divergir de novo entre as duas telas (ver spec EditarColheitaAssociada).
export function podeEditarVenda(venda: Pick<VendaWithRelations, "status">): boolean {
  return venda.status === "RASCUNHO" || venda.status === "EMITIDA";
}

// ─── Compradores ──────────────────────────────────────────────────────────────

export function useCompradores(ativo?: boolean) {
  const url = ativo === undefined ? "/api/compradores" : `/api/compradores?ativo=${ativo}`;
  return useQuery<Comprador[]>({ queryKey: ["/api/compradores", ativo], queryFn: () => fetchJson<Comprador[]>(url) });
}

export function useCreateComprador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertComprador>) =>
      apiRequest("POST", "/api/compradores", data).then(r => r.json()) as Promise<Comprador>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/compradores"] }),
  });
}

export function useUpdateComprador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateComprador }) =>
      apiRequest("PATCH", `/api/compradores/${id}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/compradores"] }),
  });
}

// ─── Produtos de venda ────────────────────────────────────────────────────────

export function useProdutosVenda(ativo?: boolean) {
  const url = ativo === undefined ? "/api/produtos-venda" : `/api/produtos-venda?ativo=${ativo}`;
  return useQuery<ProdutoVenda[]>({ queryKey: ["/api/produtos-venda", ativo], queryFn: () => fetchJson<ProdutoVenda[]>(url) });
}

export function useCreateProdutoVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertProdutoVenda>) =>
      apiRequest("POST", "/api/produtos-venda", data).then(r => r.json()) as Promise<ProdutoVenda>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/produtos-venda"] }),
  });
}

export function useUpdateProdutoVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProdutoVenda }) =>
      apiRequest("PATCH", `/api/produtos-venda/${id}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/produtos-venda"] }),
  });
}

// ─── Vendas ───────────────────────────────────────────────────────────────────

export interface VendasFiltro {
  periodo?: string;
  // Só usados quando periodo === "personalizado".
  dataInicio?: string;
  dataFim?: string;
  compradorId?: string;
  status?: string;
  /** Recorta ITENS, não vendas — ver shared/vendas-itens.ts. */
  variedadeId?: string;
}

// UM construtor de query para os dois endpoints. Antes a lista recebia um objeto de filtro e o
// resumo recebia parâmetros soltos (só `periodo`), então três cards do topo descreviam um
// conjunto e a tabela descrevia outro. Duas listas de parâmetros que podem divergir é a forma
// estrutural do bug — aqui não há como passar filtro pra um e esquecer do outro.
function buildFiltroQuery(filtro: VendasFiltro): string {
  const params = new URLSearchParams();
  if (filtro.periodo) params.set("periodo", filtro.periodo);
  if (filtro.dataInicio) params.set("dataInicio", filtro.dataInicio);
  if (filtro.dataFim) params.set("dataFim", filtro.dataFim);
  if (filtro.compradorId) params.set("compradorId", filtro.compradorId);
  if (filtro.status) params.set("status", filtro.status);
  if (filtro.variedadeId) params.set("variedadeId", filtro.variedadeId);
  return params.toString();
}

function buildVendasQuery(filtro: VendasFiltro): string {
  const qs = buildFiltroQuery(filtro);
  return qs ? `/api/vendas?${qs}` : "/api/vendas";
}

export function useVendas(filtro: VendasFiltro = {}) {
  return useQuery<VendaWithRelations[]>({
    queryKey: ["/api/vendas", filtro],
    queryFn: () => fetchJson<VendaWithRelations[]>(buildVendasQuery(filtro)),
  });
}

/** Variedades ATIVAS **do Cacho**, para o seletor do filtro de Vendas.
 *
 *  Vendas é romaneio de banana: variedade de outra cultura no seletor só podia
 *  devolver lista vazia. `filtrarVariedadesDoCacho` é fail-open — variedade sem
 *  cultura resolvida continua aparecendo (ver shared/culturas.ts).
 *
 *  `fetchJson` checa `r.ok` — queryFn que não checa vira white-screen (armadilha
 *  conhecida do repo). */
export function useVariedadesAtivas() {
  return useQuery<Array<{ id: string; nome: string; ativo: boolean }>>({
    queryKey: ["/api/variedades", "ativas-cacho"],
    queryFn: async () => {
      const todas = await fetchJson<VariedadeWithCultura[]>("/api/variedades");
      return filtrarVariedadesDoCacho(todas).filter(v => v.ativo);
    },
  });
}

export function useVenda(id: string | undefined) {
  return useQuery<VendaWithRelations>({
    queryKey: ["/api/vendas", id],
    queryFn: () => fetchJson<VendaWithRelations>(`/api/vendas/${id}`),
    enabled: !!id,
  });
}

/** Recebe o MESMO objeto de filtro que `useVendas` — o topo descreve a tabela, por construção. */
export function useResumoVendas(filtro: VendasFiltro = {}) {
  const qs = buildFiltroQuery({ ...filtro, periodo: filtro.periodo ?? "mes-atual" });
  return useQuery<{
    totalVendido: number; aReceber: number; aReceberEmDivergencia: number; totalVendas: number; vendasAReceber: number; vencidasAReceber: number; porComprador: any[]; porProduto: any[];
    classificacao: {
      // Percentual SEMPRE sobre kgClassificado, nunca sobre o kg total: sobre o total, uma
      // carga com Nanica mostraria "% de 1ª" falsamente baixo (a 034 daria 39,4% em vez de
      // 86,4%). E a base sai junto, porque todo número que exclui parte do universo declara
      // quanto excluiu no mesmo lugar onde aparece.
      porClasse: Array<{ classificacao: string; kg: number; valor: number }>;
      kgClassificado: number;
      kgSemClassificacao: number;
      variedadesComClassificacao: string[];
      variedadesSemClassificacao: string[];
    };
    colhido: {
      // kg VENDIDO no recorte (soma tudo) × kg que entrou no indicador (só o alocado). Os
      // dois números têm bases diferentes de propósito, e é essa diferença que a tela mostra
      // como cobertura — foi cobertura implícita que escondeu o erro de julho.
      kgTotal: number;
      kgAlocadoTotal: number;
      porVariedade: Array<{ variedadeId: string; nome: string; kgPorCacho: number; cachos: number; kgAlocados: number }>;
      vendasSemColheitaEncontrada: number;
      // Cachos colhidos na janela abaixo que nenhuma venda reivindicou. O número que denuncia
      // associação faltando — antes disso era impossível distinguir "não vendi" de
      // "esqueci de associar".
      saldoCachos: number;
      // Duas janelas, dois papéis (≠ filtro de data, que é de VENDA):
      // `janelaSaldo` vem das LINHAS DE RATEIO (fato) — onde medir saldo;
      // `janelaRecalculo` vem das ASSOCIAÇÕES (intenção) — onde RECALCULAR. Recalcular pela
      // primeira seria ovo e galinha: quem nunca teve rateio nunca entraria na janela.
      janelaSaldo: { inicio: string; fim: string } | null;
      janelaRecalculo: { inicio: string; fim: string } | null;
    };
  }>({
    queryKey: ["/api/vendas/resumo", filtro],
    queryFn: () => fetchJson(`/api/vendas/resumo?${qs}`),
  });
}

export function useContextoColheita(inicio: string | undefined, fim: string | undefined) {
  return useQuery<{ cachos: number; porSetor: any[]; porVariedade: any[] }>({
    queryKey: ["/api/vendas/contexto-colheita", inicio, fim],
    queryFn: () => fetchJson(`/api/vendas/contexto-colheita?inicio=${inicio}&fim=${fim}`),
    enabled: !!inicio && !!fim,
  });
}

export interface ColheitaPorDiaSetor {
  data: string;
  setorId: string;
  setorNome: string;
  cachos: number;
  associadoEm: Array<{ vendaId: string; numero: string }>;
}

export function useColheitasPorDiaSetor(inicio: string | undefined, fim: string | undefined) {
  return useQuery<ColheitaPorDiaSetor[]>({
    queryKey: ["/api/vendas/colheitas-por-dia-setor", inicio, fim],
    queryFn: () => fetchJson(`/api/vendas/colheitas-por-dia-setor?inicio=${inicio}&fim=${fim}`),
    enabled: !!inicio && !!fim,
  });
}

// Uma linha do rateio materializado: quantos cachos daquele (dia, setor) foram nesta venda.
// `cachosAlocados` é FRACIONÁRIO — arredonde só na renderização, nunca antes de somar.
export interface LinhaRateioVenda {
  data: string;
  setorId: string;
  setorNome: string;
  variedadeId: string | null;
  variedadeNome: string | null;
  cachosGrupo: number;
  cachosAlocados: number;
  kgAlocados: number;
  kgPorCacho: number;
  outrasVendas: number;
  inocua: boolean;
  motivoInocua: string | null;
}

export function useRateioVenda(id: string | undefined) {
  return useQuery<{
    periodo: { inicio: string; fim: string };
    /** RASCUNHO não aloca — o que ele mostra é o que ACONTECERIA na emissão. */
    previa: boolean;
    /** `faixaAviso` é a régua que decidiu o aviso: aprendida da própria variedade quando há
     *  histórico (≥20 grupos), a faixa fixa 5–40 quando não há — e a tela DIZ qual usou. */
    pesoMedioPorVariedade: Array<{ variedadeId: string | null; variedadeNome: string | null; pesoTotalKg: number; valorTotal: number; cachos: number; pesoMedioKg: number | null; avisoPesoImplausivel: boolean; faixaAviso: { min: number; max: number; aprendida: boolean } }>;
    linhas: LinhaRateioVenda[];
    /** kg de itens sem variedade cadastrada: fora do rateio, e dito em voz alta. */
    kgSemVariedade: number;
    /** Quando o rateio foi calculado (ISO). `null` na prévia. Timestamp velho = número velho. */
    calculadoEm: string | null;
  }>({
    queryKey: ["/api/vendas", id, "rateio"],
    queryFn: () => fetchJson(`/api/vendas/${id}/rateio`),
    enabled: !!id,
  });
}

/** Recálculo em lote (ADMIN). A janela vem do rateio das vendas do recorte, NUNCA do filtro
 *  de data da tela — o filtro é de venda, a janela é de colheita; confundir os dois é
 *  exatamente o bug que o card do saldo tinha. */
export function useRecalcularRateio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { de: string; ate: string; dryRun?: boolean }) =>
      apiRequest("POST", "/api/cacho/rateio/recalcular", params).then(r => r.json()) as Promise<{
        linhas: number; vendasAfetadas: number; grupos: number;
        porVariedade: Array<{ variedadeId: string | null; cachosAlocados: number; kgAlocados: number }>;
        saldo: { cachosColhidos: number; cachosAlocados: number; saldoCachos: number };
      }>,
    onSuccess: () => invalidateVendas(qc),
  });
}

function invalidateVendas(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["/api/vendas"] });
  qc.invalidateQueries({ queryKey: ["/api/vendas/resumo"] });
}

export function useCreateVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InsertVenda) =>
      apiRequest("POST", "/api/vendas", data).then(r => r.json()) as Promise<VendaWithRelations>,
    onSuccess: () => invalidateVendas(qc),
  });
}

export function useUpdateVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVenda }) =>
      apiRequest("PATCH", `/api/vendas/${id}`, data).then(r => r.json()) as Promise<VendaWithRelations>,
    onSuccess: () => invalidateVendas(qc),
  });
}

export function useEmitirVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/vendas/${id}/emitir`, {}).then(r => r.json()),
    onSuccess: () => invalidateVendas(qc),
  });
}

/**
 * "Marcar como paga" — com a DATA em que o dinheiro entrou.
 *
 * ⚠️ Sem `data` o servidor usa hoje, que é o caso de quem confirma no próprio
 * dia. Informar a data importa porque é ela que grava `paga_em`, e é por
 * `paga_em` que "Recebido no mês" soma.
 */
export function usePagarVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { id: string; data?: string }) =>
      apiRequest("POST", `/api/vendas/${dados.id}/pagar`, { data: dados.data }).then(r => r.json()),
    onSuccess: () => invalidateVendas(qc),
  });
}

export function useDespagarVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/vendas/${id}/despagar`, {}).then(r => r.json()),
    onSuccess: () => invalidateVendas(qc),
  });
}

export function useCancelarVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/vendas/${id}/cancelar`, {}).then(r => r.json()),
    onSuccess: () => invalidateVendas(qc),
  });
}

export function useExcluirVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/vendas/${id}`, {}),
    onSuccess: () => invalidateVendas(qc),
  });
}
